/**
 * Ross "Not Implemented" FYI card
 *
 * A week after the seller accepts a recommendation, Ross analyzes its effect
 * on velocity (rule VelocityAfterAcceptance). When he finds the change was
 * never actually applied — accepted, but the price/PPC never moved — there is
 * nothing to measure, so instead of the before/after analysis the seller gets
 * a "For your info" note. Listed in the "For your info" section of
 * MultiDecisionAccordion (agents view only). Same Ross image and
 * "Mark as Reviewed" behavior as the regular analysis (RossReviewable).
 *
 * features.not_implemented lists what Ross checked, one entry per change:
 * { change: "price" | "ppc", target, actual, budget_change?, currency,
 *   message }. PPC amounts are daily spend; amounts are in `currency` (the
 * marketplace's, e.g. CAD), not necessarily USD.
 */

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import React from "react";

import { AGENT_INFO } from "../../constants";

import {
  formatDateOrdinal,
  getRossActionDescription,
} from "./RossDecisionCard";
import RossReviewable from "./RossReviewable";

const FYI_COLOR = "#1565c0";

const isNumber = (value) => value != null && !Number.isNaN(Number(value));

// "$20.98", "CA$10.37"; signed adds "+"/"-" (for budget deltas)
const formatMoney = (value, currency, { signed = false } = {}) => {
  let formatted;
  try {
    formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(Math.abs(Number(value)));
  } catch {
    formatted = `${Math.abs(Number(value)).toFixed(2)} ${currency}`;
  }
  const sign = Number(value) < 0 ? "-" : signed ? "+" : "";
  return `${sign}${formatted}`;
};

// One row per change Ross checked: target vs what actually happened. Falls
// back to the backend's message when the numbers are missing.
const getNotImplementedRows = (item) => {
  const isPpc = item.change === "ppc";
  const perDay = isPpc ? "/day" : "";
  if (!isNumber(item.target) || !isNumber(item.actual)) {
    return { label: isPpc ? "PPC" : "Price", details: [item.message || ""] };
  }
  const details = [
    `Target ${formatMoney(item.target, item.currency)}${perDay}`,
    `Actual${isPpc ? " avg." : ""} ${formatMoney(
      item.actual,
      item.currency
    )}${perDay}`,
  ];
  // Only worth calling out when budgets actually moved
  if (isPpc && isNumber(item.budget_change) && Number(item.budget_change)) {
    details.push(
      `Campaign budgets ${formatMoney(item.budget_change, item.currency, {
        signed: true,
      })}/day`
    );
  }
  return { label: isPpc ? "PPC" : "Price", details };
};

const RossNotImplementedCard = ({
  asin,
  sku,
  executionId,
  store,
  marketplace,
  rossData,
  onRemove,
}) => {
  const features = rossData?.features || {};
  const decision = rossData?.decision || {};

  const originalAgent = features.original_agent || "iris";
  const originalAgentName =
    AGENT_INFO[originalAgent.toLowerCase()]?.name ||
    originalAgent.charAt(0).toUpperCase() + originalAgent.slice(1);
  const originalAction = features.original_action || "";
  const notImplemented = Array.isArray(features.not_implemented)
    ? features.not_implemented
    : [];
  const isPpcAction = originalAction.toLowerCase().includes("ppc");
  // The recommended change, in the marketplace currency when Ross reports it.
  // The starting value can be missing (e.g. no PPC spend history), then only
  // the target is shown.
  const actionCurrency = notImplemented.find(
    (item) => item.change === (isPpcAction ? "ppc" : "price")
  )?.currency;
  const changeFrom = isPpcAction
    ? features.original_ppc
    : features.original_price;
  const changeTo = isPpcAction ? features.target_ppc : features.target_price;
  const perDay = isPpcAction ? "/day" : "";
  const changeDisplay = !isNumber(changeTo)
    ? ""
    : isNumber(changeFrom)
    ? `: ${formatMoney(changeFrom, actionCurrency)} → ${formatMoney(
        changeTo,
        actionCurrency
      )}${perDay}`
    : ` to ${formatMoney(changeTo, actionCurrency)}${perDay}`;
  // The backend sends the delta on the decision for this outcome; older
  // analyses only had it in features
  const velocityDeltaPercentage =
    decision.velocity_delta_percentage ?? features.velocity_delta_percentage;
  const hasVelocity = isNumber(velocityDeltaPercentage);
  const hasVelocityValues =
    isNumber(features.velocity_pre_7d) && isNumber(features.velocity_post_7d);
  const bold = { fontWeight: 700, color: "#111827" };

  return (
    <RossReviewable
      asin={asin}
      sku={sku}
      executionId={executionId}
      store={store}
      marketplace={marketplace}
      rossData={rossData}
      onRemove={onRemove}
      // FYI look; overflow hidden crops the Ross image at the corner
      sx={{
        borderRadius: 2,
        backgroundColor: "#f3f8fd",
        border: "1px solid #dbe9f8",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, pr: 4 }}>
        <InfoOutlinedIcon sx={{ color: FYI_COLOR, fontSize: 20, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: "#374151", lineHeight: 1.5 }}
          >
            {features.original_decision_date && (
              <>
                On the{" "}
                <Box component="span" sx={bold}>
                  {formatDateOrdinal(features.original_decision_date)}
                </Box>{" "}
              </>
            )}
            you accepted {/^[aeiou]/i.test(originalAgentName) ? "an" : "a"}{" "}
            {originalAgentName} recommendation
            {originalAction && (
              <>
                {" "}
                to{" "}
                <Box component="span" sx={bold}>
                  {getRossActionDescription(originalAction)}
                  {changeDisplay}
                </Box>
              </>
            )}
            , but {AGENT_INFO.ross.name} found it{" "}
            <Box component="span" sx={{ ...bold, color: FYI_COLOR }}>
              was never implemented
            </Box>
            , so there is no result to analyze.
          </Typography>

          {notImplemented.length > 0 && (
            <Box
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 1.5,
                rowGap: 0.5,
              }}
            >
              {notImplemented.map((item, index) => {
                const { label, details } = getNotImplementedRows(item);
                return (
                  <React.Fragment key={`${item.change}-${index}`}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: "#111827" }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#374151" }}>
                      {details.join(" · ")}
                    </Typography>
                  </React.Fragment>
                );
              })}
            </Box>
          )}

          {hasVelocity && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.75, color: "#6b7280" }}
            >
              Meanwhile, 7-day avg. velocity went{" "}
              {hasVelocityValues &&
                `${Number(features.velocity_pre_7d).toFixed(2)} → ${Number(
                  features.velocity_post_7d
                ).toFixed(2)} `}
              ({Number(velocityDeltaPercentage) > 0 ? "+" : ""}
              {Number(velocityDeltaPercentage).toFixed(1)}%).
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.25, color: "#6b7280" }}
          >
            If you still want this change, apply it in Seller Central.
          </Typography>
        </Box>
      </Box>
    </RossReviewable>
  );
};

export default RossNotImplementedCard;
